import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import NursingStation1 from "../assets/images/Nursing-station/Nursing-station-1.jpg";
import NursingStation2 from "../assets/images/Nursing-station/Nursing-station-2.webp";
import NursingStation3 from "../assets/images/Nursing-station/Nursing-station-3.jpg";
import NursingStation4 from "../assets/images/Nursing-station/Nursing-station-4.webp";
import NursingStation5 from "../assets/images/Nursing-station/Nursing-station-5.webp";
import NursingStation6 from "../assets/images/Nursing-station/Nursing-station-6.jpg";
import NursingStation7 from "../assets/images/Nursing-station/Nursing-station-7.webp";
import NursingStation8 from "../assets/images/Nursing-station/Nursing-station-8.jpg";
import NursingStation9 from "../assets/images/Nursing-station/Nursing-station-9.jpg";
import SEO from "../components/SEO";

/**
 * Nursing Station Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Coverage),
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const nursingStations = [
  {
    name: "Central Ward Nursing Station",
    zone: "Level 1",
    size: "20 sq.m",
    spec: "Covers 6 Beds",
    tag: "General Ward",
    note: "360° sightlines to patient bays, integrated nurse-call panel, medication prep counter.",
    image: NursingStation1,
  },
  {
    name: "ICU Central Monitoring Station",
    zone: "Level 3",
    size: "24 sq.m",
    spec: "Covers 6–8 Beds",
    tag: "ICU",
    note: "Multi-bed vitals dashboard, central alarm console, direct line-of-sight to every bay.",
    image: NursingStation2,
  },
  {
    name: "Pediatric Ward Nursing Pod",
    zone: "Level 1",
    size: "16 sq.m",
    spec: "Covers 4 Beds",
    tag: "Pediatric",
    note: "Child-friendly counter height, parent-communication window, soft ambient lighting.",
    image: NursingStation3,
  },
  {
    name: "Maternity Ward Nursing Station",
    zone: "Level 1",
    size: "18 sq.m",
    spec: "Covers 5 Beds",
    tag: "Maternity",
    note: "Adjacent to rooming-in bays, dedicated breastfeeding support counter, baby-warmer visibility.",
    image: NursingStation4,
  },
  {
    name: "Emergency Department Triage Desk",
    zone: "Ground Floor",
    size: "14 sq.m",
    spec: "Triage Hub",
    tag: "Emergency",
    note: "Direct ambulance-bay sightline, rapid documentation counter, dual-monitor charting station.",
    image: NursingStation5,
  },
  {
    name: "OT Complex Nursing Coordination Desk",
    zone: "Level 3",
    size: "12 sq.m",
    spec: "OT Coordination",
    tag: "OT Complex",
    note: "Real-time OT status board, scrub-in/out tracking, direct intercom to each theatre.",
    image: NursingStation6,
  },
  {
    name: "Oncology Day-Care Nursing Station",
    zone: "Level 1",
    size: "16 sq.m",
    spec: "Covers 8 Chairs",
    tag: "Oncology",
    note: "Chemotherapy administration tracking, infusion-pump monitoring desk, medication safety lockers.",
    image: NursingStation7,
  },
  {
    name: "Step-Down / HDU Nursing Pod",
    zone: "Level 2",
    size: "14 sq.m",
    spec: "Covers 6 Beds",
    tag: "Step-Down",
    note: "Telemetry monitoring wall, transitional-care documentation counter, mobile workstation docks.",
    image: NursingStation8,
  },
  {
    name: "Multi-Floor Centralized Nursing Hub",
    zone: "Ground Floor",
    size: "30 sq.m",
    spec: "Hospital-Wide",
    tag: "Centralized Hub",
    note: "Integrated dashboard across departments, staff briefing corner, shift-handover counter.",
    image: NursingStation9,
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function NursingStationCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NursingStationDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .nurse-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .nurse-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .nurse-grid-3x3 { grid-template-columns: 1fr; } }
        .nurse-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .nurse-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .nurse-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="nurse-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {nursingStations.map((item, i) => (
            <NursingStationCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="nurse-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="nurse-modal-inner">
            <button
              className="nurse-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}